import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const Successful = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  // Override body background for this page
  useEffect(() => {
    const originalBackground = document.body.style.background;
    document.body.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)';
    return () => {
      document.body.style.background = originalBackground;
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown === 1) {
          clearInterval(intervalId);
          navigate("/");
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [navigate]);

  const progress = ((5 - countdown) / 5) * 100;

  return (
    <PageContainer>
      <SuccessCard>
        <IconWrapper>
          <CheckmarkCircle>
            <Checkmark viewBox="0 0 52 52">
              <CheckmarkPath d="M14 27l7 7 16-16" />
            </Checkmark>
          </CheckmarkCircle>
        </IconWrapper>

        <ContentSection>
          <TitleText>Registration Successful!</TitleText>
          <SubText>Your account has been created successfully</SubText>
        </ContentSection>

        <CountdownWrapper>
          <ProgressRing>
            <svg viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="6"
              />
              <ProgressCircle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeWidth="6"
                $progress={progress}
              />
            </svg>
            <CountdownNumber>{countdown}</CountdownNumber>
          </ProgressRing>
          <RedirectText>Redirecting to home in {countdown} seconds...</RedirectText>
        </CountdownWrapper>

        <SkipButton onClick={() => navigate("/")}>
          Go to Home Now →
        </SkipButton>
      </SuccessCard>

      <Confetti>
        {[...Array(12)].map((_, i) => (
          <ConfettiPiece key={i} $delay={i * 0.1} $color={getConfettiColor(i)} />
        ))}
      </Confetti>
    </PageContainer>
  );
};

const getConfettiColor = (index) => {
  const colors = ['#1a73e8', '#34a853', '#fbbc05', '#ea4335', '#9c27b0', '#00bcd4'];
  return colors[index % colors.length];
};

export default Successful;

// Keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const drawCheck = keyframes`
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const confettiFall = keyframes`
  0% {
    transform: translateY(-100vh) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-bottom: 60px;
  overflow: hidden;
`;

const SuccessCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 24px;
  padding: 48px 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
  z-index: 1;
`;

const IconWrapper = styled.div`
  margin-bottom: 24px;
  animation: ${scaleIn} 0.6s ease-out 0.2s both;
`;

const CheckmarkCircle = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #34a853 0%, #2d8f47 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(52, 168, 83, 0.3);
`;

const Checkmark = styled.svg`
  width: 40px;
  height: 40px;
`;

const CheckmarkPath = styled.path`
  fill: none;
  stroke: white;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 50;
  animation: ${drawCheck} 0.6s ease-out 0.5s both;
`;

const ContentSection = styled.div`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;
`;

const TitleText = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SubText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const CountdownWrapper = styled.div`
  margin-bottom: 24px;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
`;

const ProgressRing = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 16px;
  position: relative;
  animation: ${pulse} 2s ease-in-out infinite;

  svg {
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
  }
`;

const ProgressCircle = styled.circle`
  stroke: #1a73e8;
  stroke-linecap: round;
  stroke-dasharray: 283;
  stroke-dashoffset: ${props => 283 - (283 * props.$progress) / 100};
  transition: stroke-dashoffset 0.5s ease;
`;

const CountdownNumber = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
`;

const RedirectText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #888;
  margin: 0;
`;

const SkipButton = styled.button`
  background: none;
  border: 2px solid #e8e8e8;
  padding: 12px 24px;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeInUp} 0.6s ease-out 0.5s both;

  &:hover {
    background: #1a1a1a;
    border-color: #1a1a1a;
    color: white;
    transform: translateY(-1px);
  }
`;

const Confetti = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
`;

const ConfettiPiece = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: ${props => props.$color};
  left: ${() => Math.random() * 100}%;
  animation: ${confettiFall} 3s linear ${props => props.$delay}s infinite;
  opacity: 0.8;
  border-radius: 2px;

  &:nth-child(odd) {
    width: 8px;
    height: 12px;
    border-radius: 0;
  }
`;
